var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Button, Loader, Menu, useToast } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { useHistory } from 'react-router-dom';
import { useApprovalWorkflow } from 'app/domains/approval';
import { useEsigning } from 'app/domains/esigning';
import { DocumentType } from 'shared/domains/apollo/generated/types';
import { EditDocumentDialog, documentFlush } from 'shared/domains/documents';
import { SupportedFileMimeTypes } from 'shared/domains/filesystem';
import { EditApprovalRequestedButton } from './EditApprovalRequestedButton';
import { EditButtonDocument } from './EditButton.gql';
import { useStartEditingWithTemplate } from './useStartEditingWithTemplate';
export function EditButton(props) {
    const { documentId } = props;
    const history = useHistory();
    const { data, loading, error } = useQuery(EditButtonDocument, {
        variables: { id: documentId },
    });
    const { document } = data || {};
    const { draftId, draft, type, files } = document || {};
    const hasTemplateAccess = Boolean(draft === null || draft === void 0 ? void 0 : draft.templateVersion);
    const hasDocxFile = files === null || files === void 0 ? void 0 : files.find((file) => file.mimeType === SupportedFileMimeTypes.DOCX);
    const isDocx = type === DocumentType.Uploaded && Boolean(hasDocxFile);
    const isComposed = type === DocumentType.Composed;
    const isRtcAvailable = type === DocumentType.Composed || type === DocumentType.Freetext;
    const { startEditingWithTemplate } = useStartEditingWithTemplate();
    const { addToast } = useToast();
    const { approvalRequested } = useApprovalWorkflow();
    const { unknown, pending, creating, cancelling, submittingCreation, submittingCancellation } = useEsigning();
    const [openEditDocumentDialog, setEditDocumentDialogState] = useState(false);
    const showEditDocumentDialog = () => setEditDocumentDialogState(true);
    const hideEditDocumentDialog = () => setEditDocumentDialogState(false);
    const disabled = Boolean(error) || pending || creating || cancelling || submittingCreation || submittingCancellation || unknown;
    const allowEdit = isComposed || isDocx;
    const handleEditAsFreeText = () => {
        showEditDocumentDialog();
    };
    const handleTemplateEdit = () => __awaiter(this, void 0, void 0, function* () {
        history.push(`/draft/${draftId}`);
        if (!isRtcAvailable) {
            return;
        }
        try {
            documentFlush({ state: 'initialized' });
            yield startEditingWithTemplate(documentId);
        }
        catch (_a) {
            addToast({
                appearance: 'danger',
                title: t({
                    comment: 'Edit document with template error - The document is deleted - toast title',
                    message: 'Could not edit document',
                }),
                message: t({
                    comment: 'Edit document with template error - The document is deleted - toast message',
                    message: 'The document has been deleted',
                }),
            });
        }
    });
    if (loading) {
        return (<Button disabled type="button" appearance="primary">
        <Loader />
      </Button>);
    }
    if (approvalRequested) {
        return <EditApprovalRequestedButton />;
    }
    if (!allowEdit) {
        return null;
    }
    return (<Fragment>
      <Menu.Root target={<Button size="xs" type="button" appearance="primary" disabled={disabled}>
            <Falcon icon="pen"/>
            <Trans comment="Label for edit document">Edit</Trans>
          </Button>}>
        {isComposed && (<Menu.Item onClick={handleTemplateEdit} disabled={!hasTemplateAccess}>
            <Falcon icon="list"/>
            <Menu.ItemText primary={t({ message: 'Edit with template', comment: 'Label for edit document with template' })}/>
          </Menu.Item>)}
        <Menu.Item onClick={handleEditAsFreeText}>
          <Falcon icon="a"/>
          <Menu.ItemText primary={t({ message: 'Edit as free-text', comment: 'Label for edit document as free-text' })}/>
        </Menu.Item>
      </Menu.Root>
      {openEditDocumentDialog && <EditDocumentDialog documentId={documentId} onClose={hideEditDocumentDialog}/>}
    </Fragment>);
}
